/**
 * SMS consent modal, shown once to existing users who already have a
 * phone_e164 on file but never made a deliberate SMS opt-in/out decision.
 *
 * TCPA compliance notes:
 *   - Three outcomes: Opt In, Opt Out, Dismiss (close without deciding).
 *   - All three stamp `sms_consent_prompt_seen_at` so we don't re-prompt.
 *   - Only Opt In sets `sms_consent_at` (with IP/UA for audit trail).
 *   - Only Opt Out sets `sms_opt_out_at`. Once set, future opt-in attempts
 *     require explicit user action (e.g. settings page).
 *   - Consent language must be in the modal body (not just title), and
 *     must mention frequency, opt-out method, and that it's optional.
 *
 * Trigger conditions are owned by the caller, the modal renders
 * unconditionally when its `open` prop is true.
 */

import { useState } from 'react';
import { supabase } from '../lib/supabase';

interface Props {
  open: boolean;
  userId: string;
  onClose: () => void;
}

export function SmsConsentModal({ open, userId, onClose }: Props) {
  const [submitting, setSubmitting] = useState<'in' | 'out' | 'dismiss' | null>(null);

  if (!open) return null;

  const stamp = async (decision: 'in' | 'out' | 'dismiss') => {
    if (submitting) return;
    setSubmitting(decision);

    const nowIso = new Date().toISOString();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    // Build the patch by decision. prompt_seen_at always stamped so we
    // suppress future shows even on dismiss.
    const patch: Record<string, unknown> = {
      sms_consent_prompt_seen_at: nowIso,
    };
    if (decision === 'in') {
      patch.sms_consent_at = nowIso;
      patch.sms_consent_user_agent = userAgent;
      // sms_consent_ip is server-known and not available here; admin tools
      // can backfill if legal needs it. The UA + timestamp is the minimum
      // useful audit trail from the client.
    } else if (decision === 'out') {
      patch.sms_opt_out_at = nowIso;
    }

    try {
      await supabase.from('profiles').update(patch).eq('id', userId);
    } catch (err) {
      // Silent fail, no point in surfacing a "failed to save your
      // preference" to the user; they can re-open via settings later.
      console.error('SMS consent save failed:', err);
    } finally {
      setSubmitting(null);
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sms-consent-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26,18,48,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
      onClick={() => stamp('dismiss')}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: '20px',
          maxWidth: '420px',
          width: '100%',
          padding: '28px 24px 22px',
          boxShadow: '0 8px 32px rgba(26,18,48,0.18)',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          color: '#1A1230',
        }}
      >
        <h2
          id="sms-consent-title"
          style={{
            fontFamily: "'Urbanist', system-ui, sans-serif",
            fontSize: '22px',
            fontWeight: 700,
            margin: '0 0 8px',
            lineHeight: 1.3,
          }}
        >
          Want a text when there's an update?
        </h2>

        <p style={{ fontSize: '14px', lineHeight: 1.55, color: 'rgba(26,18,48,0.75)', margin: '0 0 14px' }}>
          We can text you when Agent Loop sends a new message about your
          case, so you don't have to keep checking your inbox.
        </p>

        <div
          style={{
            background: 'rgba(233, 122, 80,0.06)',
            border: '1px solid rgba(233, 122, 80,0.15)',
            borderRadius: '10px',
            padding: '12px 14px',
            fontSize: '12px',
            lineHeight: 1.5,
            color: 'rgba(26,18,48,0.65)',
            margin: '0 0 18px',
          }}
        >
          By tapping <strong>Yes, text me</strong> you agree to receive
          automated text messages from Mediloop about your case. Message
          and data rates may apply. Reply <strong>STOP</strong> to
          unsubscribe at any time. Consent is optional and not required
          to use Mediloop.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            type="button"
            onClick={() => stamp('in')}
            disabled={submitting !== null}
            style={{
              padding: '12px 20px',
              background: '#E97A50',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontFamily: "'Urbanist', system-ui, sans-serif",
              fontSize: '15px',
              fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting === 'in' ? 'Saving…' : 'Yes, text me'}
          </button>
          <button
            type="button"
            onClick={() => stamp('out')}
            disabled={submitting !== null}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              color: 'rgba(26,18,48,0.65)',
              border: '1px solid rgba(26,18,48,0.15)',
              borderRadius: '12px',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting === 'out' ? 'Saving…' : 'No thanks'}
          </button>
          <button
            type="button"
            onClick={() => stamp('dismiss')}
            disabled={submitting !== null}
            style={{
              padding: '6px',
              background: 'transparent',
              color: 'rgba(26,18,48,0.4)',
              border: 'none',
              fontSize: '12px',
              fontFamily: "'DM Sans', sans-serif",
              cursor: submitting ? 'wait' : 'pointer',
              marginTop: '4px',
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
